import React, { useState, FormEvent, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { Address } from '../types';
import './Pages.css';

interface CreateRestaurantDto {
  name: string;
  phone?: string;
  email?: string;
  description?: string;
  address: Address;
}

const AddRestaurant: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [formData, setFormData] = useState<CreateRestaurantDto>({
    name: '',
    phone: '',
    email: '',
    description: '',
    address: {
      street: '',
      number: '',
      neighborhood: '',
      city: '',
      state: '',
      zipCode: ''
    }
  });

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddressChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      address: {
        ...(prev.address || {} as Address),
        [name]: value
      }
    }));
  };

  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 7) {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    } else {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
    }
  };

  const handlePhoneChange = (e: ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(e.target.value);
    setFormData(prev => ({
      ...prev,
      phone: formatted
    }));
  };

  const handleLogoChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo de arquivo
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      setError('Tipo de arquivo não suportado. Use JPG ou PNG.');
      return;
    }

    // Validar tamanho (máximo 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setError('Arquivo muito grande. Tamanho máximo: 5MB.');
      return;
    }

    setLogoFile(file);
    setError('');

    // Criar preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };



  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Remover formatação do telefone para enviar
      const cleanPhone = formData.phone ? formData.phone.replace(/\D/g, '') : '';
      const phoneWithCountryCode = cleanPhone ? `+55${cleanPhone}` : undefined;

      const submitData = {
        ...formData,
        phone: phoneWithCountryCode || undefined,
        email: formData.email || undefined,
        description: formData.description || undefined
      };

      const response = await api.post('/admin/restaurants', submitData);
      
      // Se houver logo, fazer upload
      if (logoFile) {
        const formDataLogo = new FormData();
        formDataLogo.append('file', logoFile);
        
        try {
          await api.post(`/admin/restaurants/${response.data.restaurantId}/logo`, formDataLogo, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
        } catch (logoErr: any) {
          console.error('Error uploading logo:', logoErr);
          // Não falhar a criação do restaurante se o upload da logo falhar
          // Apenas logar o erro
        }
      }
      
      // Buscar lista atualizada de restaurantes
      const restaurantsResponse = await api.get('/admin/restaurants');
      const restaurantsList = restaurantsResponse.data;
      
      // Atualizar localStorage com a lista de restaurantes
      localStorage.setItem('restaurants', JSON.stringify(restaurantsList));
      
      // Atualizar localStorage com o novo restaurante selecionado
      localStorage.setItem('selectedRestaurantId', response.data.restaurantId);
      
      // Atualizar userData
      const userData = localStorage.getItem('userData');
      if (userData) {
        const parsedUser = JSON.parse(userData);
        const updatedUser = {
          ...parsedUser,
          restaurantId: response.data.restaurantId,
          restaurantName: response.data.name
        };
        localStorage.setItem('userData', JSON.stringify(updatedUser));
      }

      // Recarregar a página para aplicar as mudanças
      window.location.href = '/';
    } catch (err: any) {
      console.error('Error creating restaurant:', err);
      setError(err.response?.data?.message || 'Erro ao criar restaurante. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Adicionar Novo Restaurante</h1>
          <p>Cadastre um novo estabelecimento para gerenciar</p>
        </div>
      </div>

      <div className="profile-form">
        {error && (
          <div className="form-error">
            <span>⚠️</span>
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Nome do Restaurante *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              disabled={loading}
              placeholder="Ex: Pizzaria Bella Napoli"
            />
          </div>

          <div className="form-group">
            <label>Telefone do Restaurante (opcional)</label>
            <div className="phone-input-group">
              <span className="phone-prefix">+55</span>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handlePhoneChange}
                disabled={loading}
                placeholder="(11) 98765-4321"
                maxLength={15}
              />
            </div>
            <small className="form-hint">
              📱 Telefone comercial do restaurante. Este é diferente do seu telefone de login.
              Se não informado, será usado o telefone da sua conta de acesso.
            </small>
          </div>

          <div className="form-group">
            <label>E-mail do Restaurante (opcional)</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              disabled={loading}
              placeholder="contato@restaurante.com.br"
            />
            <small className="form-hint">
              📧 E-mail comercial do restaurante. Este é independente do seu e-mail de login (se houver).
            </small>
          </div>

          <div className="form-group">
            <label>Descrição</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={4}
              disabled={loading}
              placeholder="Descreva seu estabelecimento..."
            />
          </div>

          <div className="form-group">
            <label>Logo do Restaurante (opcional)</label>
            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png"
              onChange={handleLogoChange}
              disabled={loading}
            />
            {logoPreview && (
              <div style={{ marginTop: '10px' }}>
                <img 
                  src={logoPreview} 
                  alt="Preview" 
                  style={{ 
                    maxWidth: '200px', 
                    maxHeight: '200px', 
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    padding: '4px'
                  }} 
                />
              </div>
            )}
            <small className="form-hint">
              📷 Tamanho recomendado: 400x400 pixels. Formatos aceitos: JPG, PNG (máx. 5MB).
              <br />
              ⚠️ A imagem será redimensionada automaticamente para 400x400 pixels mantendo as proporções.
            </small>
          </div>

          <div className="form-group">
            <label>Endereço</label>
            <input
              type="text"
              name="street"
              placeholder="Rua"
              value={formData.address.street}
              onChange={handleAddressChange}
              disabled={loading}
            />
            <div className="form-row">
              <input
                type="text"
                name="number"
                placeholder="Número"
                value={formData.address.number}
                onChange={handleAddressChange}
                disabled={loading}
              />
              <input
                type="text"
                name="neighborhood"
                placeholder="Bairro"
                value={formData.address.neighborhood}
                onChange={handleAddressChange}
                disabled={loading}
              />
            </div>
            <div className="form-row">
              <input
                type="text"
                name="city"
                placeholder="Cidade"
                value={formData.address.city}
                onChange={handleAddressChange}
                disabled={loading}
              />
              <input
                type="text"
                name="state"
                placeholder="Estado (ex: SP)"
                value={formData.address.state}
                onChange={handleAddressChange}
                disabled={loading}
                maxLength={2}
              />
            </div>
            <input
              type="text"
              name="zipCode"
              placeholder="CEP"
              value={formData.address.zipCode}
              onChange={handleAddressChange}
              disabled={loading}
              maxLength={9}
            />
          </div>

          <div className="form-actions">
            <button 
              type="button" 
              className="btn-secondary" 
              onClick={() => navigate('/')}
              disabled={loading}
            >
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Criando...' : 'Criar Restaurante'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddRestaurant;

